import { useState } from "react";
import { KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const inputCls = "w-full bg-white border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

export default function ChangePasswordDialog({ open, onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const strong = form.new_password.length >= 12 && /[A-Za-z]/.test(form.new_password) && /\d/.test(form.new_password);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) return toast.error("New password needs 12+ characters with letters and numbers");
    if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      localStorage.setItem("cr_admin_token", r.data.token);
      toast.success("Password changed. Use the new one next time you log in.");
      setForm({ current_password: "", new_password: "", confirm: "" });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-md bg-sand border-ink/10" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-ink flex items-center gap-2"><KeyRound size={20} className="text-terra" /> Change admin password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" autoComplete="current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} placeholder="Current password" className={inputCls} data-testid="change-password-current" />
          <input type="password" autoComplete="new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="New password (12+ chars, letters & numbers)" className={inputCls} data-testid="change-password-new" />
          <input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="Repeat new password" className={inputCls} data-testid="change-password-confirm" />
          <div className="h-1.5 rounded-full bg-ink/10 overflow-hidden"><div className={`h-full transition-all ${strong ? "bg-emerald-500 w-full" : form.new_password.length > 6 ? "bg-gold w-1/2" : "bg-terra w-1/5"}`} data-testid="change-password-strength" /></div>
          <p className="text-[11px] text-ink/50">Tip: the password also gets reset whenever you change the ADMIN_PASSWORD secret in Emergent and republish.</p>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={busy} className="flex-1 bg-ink text-sand font-semibold py-3 rounded-full hover:bg-terra transition-colors disabled:opacity-60" data-testid="change-password-submit">{busy ? "Saving…" : "Update password"}</button>
            <button type="button" onClick={onClose} className="px-4 rounded-full border border-ink/15 text-ink/60 hover:bg-white" data-testid="change-password-cancel"><X size={16} /></button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
